import { useEffect, useState } from 'react';

/** True while the viewport matches `query`, and follows it as the window changes. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** The width at which `tbl-stack` tables turn into cards (styles.css). */
const PHONE = '(max-width: 640px)';

/**
 * Splits a list into pages, with a page size that depends on the screen.
 *
 * A table on a desktop can show a long list in one go, but the same rows drawn
 * as cards on a phone make a page that is screens long. So the size is given
 * per class of screen: pass `Infinity` for "everything" where paging would only
 * be a regression (a desktop table that has always shown every row).
 *
 * The page resets to the first when the list changes underneath it - a new
 * filter, a refetch that shortened it - rather than showing an empty page.
 */
export function usePaged<T>(items: readonly T[], size: { phone: number; desktop: number }) {
  const phone = useMediaQuery(PHONE);
  const pageSize = phone ? size.phone : size.desktop;
  const pages = Number.isFinite(pageSize) ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;

  const [requested, setPage] = useState(1);
  const page = Math.min(requested, pages);

  useEffect(() => {
    setPage(1);
  }, [items.length, pageSize]);

  const from = Number.isFinite(pageSize) ? (page - 1) * pageSize : 0;
  const rows = Number.isFinite(pageSize) ? items.slice(from, from + pageSize) : [...items];

  return {
    rows,
    page,
    pages,
    total: items.length,
    from: items.length === 0 ? 0 : from + 1,
    to: from + rows.length,
    setPage,
  };
}
